/**
 * Offline queue for POD photo & signature uploads.
 *
 * Uses IndexedDB (survives page refresh + is per-origin).
 * Each entry: { id, dn_id, dn_number, photo_type, notes, blob, filename,
 *   created_at, attempts, last_error }
 *
 * Public API:
 *   enqueuePODUpload({ dnId, dnNumber, photoType, notes, file }) -> id
 *   listQueue() -> [entries]
 *   getPendingCount() -> number
 *   removeFromQueue(id)
 *   syncNow(uploadFn) -> { successes, failures }
 *   subscribe(callback) -> unsubscribe   // callback fires on any queue change
 */

const DB_NAME = 'nexus-pod-offline';
const DB_VERSION = 1;
const STORE = 'pod_uploads';

let _dbPromise = null;

const openDB = () => {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('dn_id', 'dn_id', { unique: false });
        store.createIndex('created_at', 'created_at', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
};

const withStore = async (mode, cb) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    let result;
    Promise.resolve(cb(store)).then((r) => { result = r; }).catch(reject);
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error);
  });
};

// --- Subscribers -----------------------------------------------------------

const _listeners = new Set();

const _notify = () => _listeners.forEach((fn) => {
  try { fn(); } catch { /* ignore */ }
});

export const subscribe = (fn) => {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
};

// --- Public API -----------------------------------------------------------

export const enqueuePODUpload = async ({ dnId, dnNumber, photoType, notes, file, gps, itemId }) => {
  const id = `q_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const entry = {
    id,
    dn_id: dnId,
    dn_number: dnNumber || null,
    photo_type: photoType,
    item_id: itemId || null,
    notes: notes || '',
    blob: file,                 // File is a Blob subclass, IDB stores it fine
    filename: file.name || `${photoType}.jpg`,
    mime: file.type || 'image/jpeg',
    gps: gps || null,           // { latitude, longitude, accuracy_m, captured_at }
    created_at: new Date().toISOString(),
    attempts: 0,
    last_error: null,
  };
  await withStore('readwrite', (store) => store.add(entry));
  _notify();
  return id;
};

export const listQueue = async () =>
  withStore('readonly', (store) => new Promise((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  }));

export const getPendingCount = async () => {
  const items = await listQueue();
  return items.length;
};

export const removeFromQueue = async (id) => {
  await withStore('readwrite', (store) => store.delete(id));
  _notify();
};

const _updateEntry = async (id, patch) => {
  await withStore('readwrite', (store) => new Promise((resolve, reject) => {
    const req = store.get(id);
    req.onsuccess = () => {
      const existing = req.result;
      if (!existing) return resolve();
      Object.assign(existing, patch);
      const put = store.put(existing);
      put.onsuccess = () => resolve();
      put.onerror = () => reject(put.error);
    };
    req.onerror = () => reject(req.error);
  }));
  _notify();
};

/**
 * Iterate the queue and try to upload each entry via `uploadFn(entry)`.
 * `uploadFn` must return a Promise that resolves on success and rejects on failure.
 * Entries that succeed are removed. Entries that fail are kept with attempts+1.
 */
let _syncing = false;
export const syncNow = async (uploadFn) => {
  if (_syncing) return { successes: 0, failures: 0, skipped: true };
  _syncing = true;
  let successes = 0;
  let failures = 0;
  try {
    const items = await listQueue();
    for (const item of items) {
      try {
        await uploadFn(item);
        await removeFromQueue(item.id);
        successes += 1;
      } catch (e) {
        failures += 1;
        await _updateEntry(item.id, {
          attempts: (item.attempts || 0) + 1,
          last_error: e?.message || String(e),
        });
      }
    }
  } finally {
    _syncing = false;
  }
  return { successes, failures };
};
